import { useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { BookmarkPlus, Trash2, Check, X, LayoutTemplate } from 'lucide-react';

// Reusable quote defaults: payment terms, overhead, labour rates, VAT, margin.
// Only rendered for Pro users on the Estimator.
export default function QuoteTemplateBar({ current, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/quote-templates').then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = (id) => {
    setSelected(id);
    const t = templates.find((x) => x.id === id);
    if (t) { onApply(t); toast.success(`Applied "${t.name}"`); }
  };

  const saveTemplate = async () => {
    if (!newName.trim()) { toast.error('Give the template a name'); return; }
    setBusy(true);
    try {
      await api.post('/quote-templates', {
        name: newName.trim(),
        payment_terms: current.paymentTerms,
        overhead_per_day: parseFloat(current.overheadsPerDay) || 0,
        labour_hours: parseFloat(current.labourHours) || 0,
        labour_rate: parseFloat(current.labourRate) || 0,
        labour_days: parseFloat(current.labourDays) || 0,
        labour_day_rate: parseFloat(current.labourDayRate) || 0,
        extra_materials: parseFloat(current.extraMaterials) || 0,
        items: (current.items || []).filter((it) => (it.description || '').trim() || parseFloat(it.unit_price) || parseFloat(it.qty) > 1),
        vat_rate: parseFloat(current.vat) || 0,
        margin_pct: parseFloat(current.margin) || 0,
        quote_validity_days: parseInt(current.quoteValidityDays) || 30,
        pdf_template: current.pdfTemplate || 'detailed',
        notes: current.notes || '',
        terms: current.terms || '',
      });
      toast.success('Template saved');
      setNewName(''); setShowSave(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/quote-templates/${id}`); if (selected === id) setSelected(''); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="tm-card p-4" data-testid="quote-template-bar">
      <div className="flex items-center gap-2 mb-3">
        <LayoutTemplate size={16} className="text-brand" />
        <span className="font-display font-bold uppercase tracking-tight text-sm">Quote Templates</span>
        <span className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Saves line items, labour, materials, terms &amp; rates</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="quote-template-select"
          value={selected}
          onChange={(e) => apply(e.target.value)}
          className="tm-input bg-white flex-1 min-w-[180px] text-sm"
        >
          <option value="">— Apply a saved template —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button
          data-testid="quote-template-save-btn"
          onClick={() => setShowSave((s) => !s)}
          className="tm-btn text-xs min-h-0 py-2"
        >
          <BookmarkPlus size={14}/> Save current as template
        </button>
      </div>

      {showSave && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            data-testid="quote-template-name-input"
            className="tm-input flex-1 min-w-[180px] text-sm"
            placeholder="Template name e.g. Standard 50/50 terms"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveTemplate()}
          />
          <button data-testid="quote-template-confirm-save" onClick={saveTemplate} disabled={busy} className="tm-btn text-xs min-h-0 py-2"><Check size={14}/> {busy ? 'Saving…' : 'Save'}</button>
          <button onClick={() => setShowSave(false)} className="tm-btn tm-btn-ghost text-xs min-h-0 py-2"><X size={14}/></button>
        </div>
      )}

      {templates.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {templates.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-2 bg-slate-100 border border-slate-300 px-2 py-1 text-xs">
              <span className="font-medium">{t.name}</span>
              <button data-testid={`quote-template-delete-${t.id}`} onClick={() => del(t.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12}/></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
